import { ShoppingCartOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  useGetProductCategories,
  useGetProducts,
  useGetStoreDetailsForOnlineOrder,
} from "../../api/api.get";
import EmptyComponent from "../../components/empty.component";
import { FeaturesItems } from "../../components/featured-items";
import { OrgDataComponent } from "../../components/org.data.component";
import ProductViewBottomSheet from "../../components/product.view.botomsheet";
import { RowItemForReservation } from "../../components/row.item";
import { useStoreContext } from "../../providers/store.provider";
import { IProductOnline } from "../../types/types";
import { capitalizeWithTrunc } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";
import ReservationsCheckout from "./reservations-checkout";

export default function Reservations() {
  const { id } = useParams();
  const [openBottomSheet, setOpenBottomSheet] = useState<IProductOnline>();

  const { data: categoriesData } = useGetProductCategories(id);

  const { data: storeDetailsData, error } =
    useGetStoreDetailsForOnlineOrder(id);
  const [searchText, setSearchText] = useState<string>("");

  const [debouncedProductNameSearchText] = useDebounce(searchText, 300);

  const { data: productsByNameData, isLoading: isSearchLoading } =
    useGetProducts(id, debouncedProductNameSearchText);

  const [productsToCheckout, setProductsToCheckout] = useState<
    IProductOnline[]
  >([]);
  const [items, setItems] = useState<IProductOnline[]>([]);

  const navigate = useNavigate();
  const { currency } = useStoreContext();
  const [confirmCheckout, setConfirmCheckout] = useState<boolean>(false);

  useEffect(() => {
    if (productsByNameData) {
      setItems(productsByNameData);
    } else {
    }
  }, [productsByNameData]);

  const handleRemoveItems = (item: IProductOnline) => {
    const _items = [...productsToCheckout];
    const index = _items.findIndex((p) => p._id === item._id);
    _items.splice(index, 1);
    setProductsToCheckout(_items);
  };

  const itemTotal: number = productsToCheckout.reduce(
    (acc, item) => acc + (item.price?.value ?? 0),
    0,
  );

  const handleAddItems = (item: IProductOnline) => {
    const existingProductsToCheckout = [...productsToCheckout];
    existingProductsToCheckout.push(item);
    setProductsToCheckout(existingProductsToCheckout);
  };

  useEffect(() => {
    const _error: any = error;
    if (_error) {
      if (_error.status === 404) {
        navigate("/not-found");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  const [showTextOnly, setShowTextOnly] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowTextOnly(window.scrollY > 300); // You can adjust this threshold
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSelectCategory = (categoryName: string) => {
    if (searchText === categoryName) {
      setSearchText("");
    } else {
      setSearchText(categoryName);
    }
  };

  return (
    <div
      className="mx-auto max-w-5xl min-h-screen"
      style={{
        background: storeDetailsData?.bodyBackgroundColor ?? "white",
        color: storeDetailsData?.bodyFontColor ?? "red",
      }}
    >
      <ProductViewBottomSheet
        onAddItems={(product) => handleAddItems(product)}
        onClose={() => setOpenBottomSheet(undefined)}
        onRemoveItems={(product) => handleRemoveItems(product)}
        open={!!openBottomSheet}
        productsToCheckout={productsToCheckout}
        product={openBottomSheet}
      />

      <ReservationsCheckout
        items={productsToCheckout}
        storeDetails={storeDetailsData}
        onOK={() => {
          setConfirmCheckout(false);
          setSearchText("");
          setProductsToCheckout([]);
        }}
        onCancel={() => setConfirmCheckout(false)}
        onRemoveItems={(item) => handleRemoveItems(item)}
        open={confirmCheckout}
      />

      <div className="py-10 px-4">
        <OrgDataComponent
          storeDetailsData={storeDetailsData}
          variant="restaurant"
        />
      </div>

      <div className="p-4">
        <div>
          {categoriesData && categoriesData.length > 0 && (
            <div
              className="my-0"
              style={{ position: "sticky", top: 0, zIndex: 999 }}
            >
              <div
                style={{
                  display: "flex",
                  overflowX: "auto",
                  gap: showTextOnly ? 0 : 16,
                  padding: showTextOnly ? "0px" : "20px 0px",
                  backgroundColor: "",
                }}
              >
                {categoriesData?.map((cat, idx) => (
                  <div
                    key={idx}
                    className="item"
                    onClick={() => handleSelectCategory(cat.name)}
                    style={{
                      height: showTextOnly ? 40 : undefined,
                      padding: 2,
                      fontFamily: showTextOnly ? "sans-serif" : "inherit",
                      borderRadius: showTextOnly ? 0 : 10,
                      backgroundColor:
                        cat.name === searchText
                          ? "lightgrey"
                          : storeDetailsData?.bodyFontColor,
                      color: storeDetailsData?.bodyBackgroundColor ?? "inherit",
                      border: `1px solid ${storeDetailsData?.bodyBackgroundColor ?? "black"}`,
                    }}
                  >
                    {!showTextOnly && (
                      <img
                        src={cat.image ?? "/assets/images/no-picture.svg"}
                        alt="category"
                      />
                    )}

                    <div className="item-title">
                      {capitalizeWithTrunc(cat.name)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="my-2 flex flex-row gap-4">
            <Input.Search
              allowClear
              placeholder="Search for items..."
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              size="large"
            />

            <div className="flex flex-row gap-4 items-center">
              <strong id="total-amount">
                {currency}
                {toFixedDisplay(itemTotal)}
              </strong>

              <Button
                type="primary"
                id="checkout-btn"
                onClick={() => setConfirmCheckout(true)}
                icon={<ShoppingCartOutlined />}
              >
                {productsToCheckout.length}
              </Button>
            </div>
          </div>

          {!searchText && (
            <FeaturesItems onClick={(item) => setOpenBottomSheet(item)} />
          )}

          <Flex gap={5} className="divSpread"></Flex>

          <div className="my-4 p-0" style={{}}>
            <Skeleton active loading={isSearchLoading}>
              {items.length === 0 && (
                <EmptyComponent
                  color={storeDetailsData?.bodyFontColor ?? "inherit"}
                  title="No Items Found. Please change search terms."
                />
              )}
              {items.length > 0 &&
                items.map((item, idx) => (
                  <RowItemForReservation
                    item={item}
                    key={idx}
                    onClick={(product) => setOpenBottomSheet(product)}
                    productsToCheckout={productsToCheckout}
                  />
                ))}
            </Skeleton>
          </div>
        </div>
      </div>
    </div>
  );
}
